<template>
  <div class="quote-live-card">
    <div class="card-title-label">
      <v-icon small color="rgba(255,255,255,0.55)" class="tw-mr-1">mdi-shield-check</v-icon>
      {{ checkoutMode ? 'Your cover' : 'Live premium' }}
    </div>

    <pa-premium-summary
      :option-name="optionName"
      :duration-label="durationLabel"
      :premium="premium"
      :mock="mockEnabled"
      :cover-start-date="resolvedCoverStart"
      :cover-end-date="resolvedCoverEnd"
    />

    <div v-if="checkoutMode && quote.nationalId" class="checkout-meta">
      <div class="checkout-meta-row">
        <span>Quote ref.</span>
        <span>{{ quote.quoteId }}</span>
      </div>
    </div>

    <template v-if="hasQuoteRef && !checkoutMode">
      <div class="sidebar-actions">
        <button type="button" class="sidebar-action-btn" @click="emailOpen = true">
          <v-icon x-small color="white" class="tw-mr-1">mdi-email-outline</v-icon>
          Email
        </button>
        <button type="button" class="sidebar-action-btn" @click="pdfDialogOpen = true">
          <v-icon x-small color="white" class="tw-mr-1">mdi-download</v-icon>
          PDF
        </button>
      </div>
    </template>

    <div v-if="!checkoutMode && continueLabel" class="cta-bar">
      <button
        type="button"
        class="btn-continue"
        :disabled="!canContinue || generating"
        @click="$emit('continue')"
      >
        <v-icon v-if="generating" small color="white" class="tw-mr-1 tw-animate-spin">mdi-loading</v-icon>
        {{ generating ? 'Preparing…' : continueLabel }}
        <v-icon v-if="!generating" small color="white" class="tw-ml-1">mdi-arrow-right</v-icon>
      </button>
    </div>

    <!-- Email dialog -->
    <v-dialog v-model="emailOpen" max-width="420" overlay-color="#1E4276" overlay-opacity="0.55">
      <div class="eq-dialog">
        <div class="eq-header">
          <div class="eq-header-icon"><v-icon size="24" color="white">mdi-email-send-outline</v-icon></div>
          <div>
            <p class="eq-title">Email quote</p>
            <p class="eq-subtitle">Send this quote to your customer</p>
          </div>
          <button type="button" class="eq-close" @click="emailOpen = false"><v-icon size="18" color="white">mdi-close</v-icon></button>
        </div>
        <div class="eq-body">
          <div class="eq-field">
            <label class="eq-label">Customer name</label>
            <input v-model="customerName" class="eq-input" placeholder="e.g. John Kamau" type="text">
          </div>
          <div class="eq-field">
            <label class="eq-label">Customer email <span class="eq-req">*</span></label>
            <input v-model="customerEmail" class="eq-input" placeholder="customer@email.com" type="email">
          </div>
        </div>
        <div class="eq-footer">
          <button type="button" class="eq-btn-cancel" @click="emailOpen = false">Cancel</button>
          <button type="button" class="eq-btn-send" :disabled="!customerEmail || sending" @click="sendEmail">
            {{ sending ? 'Sending…' : 'Send quote' }}
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- PDF dialog -->
    <v-dialog v-model="pdfDialogOpen" max-width="420" overlay-color="#1E4276" overlay-opacity="0.55">
      <div class="eq-dialog">
        <div class="eq-header">
          <div class="eq-header-icon"><v-icon size="24" color="white">mdi-file-download-outline</v-icon></div>
          <div>
            <p class="eq-title">Download PDF quote</p>
            <p class="eq-subtitle">Formatted invoice — opens in a new tab</p>
          </div>
          <button type="button" class="eq-close" @click="pdfDialogOpen = false"><v-icon size="18" color="white">mdi-close</v-icon></button>
        </div>
        <div class="eq-body">
          <div class="eq-field">
            <label class="eq-label">Customer name on quote</label>
            <input v-model="pdfClientName" class="eq-input" placeholder="e.g. John Kamau" type="text">
          </div>
          <div class="eq-field">
            <label class="eq-label">Customer email</label>
            <input v-model="pdfClientEmail" class="eq-input" placeholder="customer@email.com" type="email">
          </div>
          <p class="eq-hint">PDF includes plan, benefits, premium breakdown and intermediary details.</p>
        </div>
        <div class="eq-footer">
          <button type="button" class="eq-btn-cancel" @click="pdfDialogOpen = false">Cancel</button>
          <button type="button" class="eq-btn-send" :disabled="generatingPdf" @click="downloadPdf">
            <v-progress-circular v-if="generatingPdf" indeterminate size="14" width="2" color="#1E4276" class="tw-mr-2" />
            {{ generatingPdf ? 'Generating…' : 'Open PDF' }}
          </button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { buildPaQuoteEmailHtml, openPaQuotePdf } from '@/utils/paQuoteDocument';
import { validateEmail } from '@/utils/paValidation';
import Vue from 'vue';
import { computePaCoverEndDate } from '@/utils/paCoverDates';
import PaPremiumSummary from './PaPremiumSummary';

export default {
  name: 'PaQuoteSidebar',
  components: { PaPremiumSummary },
  props: {
    optionName: { type: String, default: '' },
    durationLabel: { type: String, default: '' },
    premium: { type: Number, default: 0 },
    generating: { type: Boolean, default: false },
    canContinue: { type: Boolean, default: false },
    quote: { type: Object, default: () => ({}) },
    mockEnabled: { type: Boolean, default: true },
    continueLabel: { type: String, default: 'Continue to proposal' },
    /** Customer payment step — hide quote actions, show cover summary only */
    checkoutMode: { type: Boolean, default: false },
    coverStartDate: { type: String, default: '' },
    coverEndDate: { type: String, default: '' },
  },
  computed: {
    ...mapGetters('auth', ['authenticatedUser']),
    agentName() {
      return this.authenticatedUser?.name || 'N/A';
    },
    hasQuoteRef() {
      return !!(this.quote && this.quote.quoteId);
    },
    resolvedCoverStart() {
      return this.coverStartDate || this.quote.coverFrom || '';
    },
    resolvedCoverEnd() {
      if (this.coverEndDate) return this.coverEndDate;
      if (this.resolvedCoverStart && this.quote.courseDuration) {
        return computePaCoverEndDate(this.resolvedCoverStart, this.quote.courseDuration);
      }
      return '';
    },
  },
  data() {
    return {
      emailOpen: false,
      pdfDialogOpen: false,
      customerName: '',
      customerEmail: '',
      pdfClientName: '',
      pdfClientEmail: '',
      sending: false,
      generatingPdf: false,
    };
  },
  watch: {
    pdfDialogOpen(open) {
      if (open) {
        this.pdfClientName = this.customerName || this.quote.proposerName || '';
        this.pdfClientEmail = this.customerEmail || this.quote.email || '';
      }
    },
  },
  methods: {
    async downloadPdf() {
      this.generatingPdf = true;
      try {
        await openPaQuotePdf(this.quote, this.pdfClientName, {
          clientEmail: this.pdfClientEmail,
          agentName: this.agentName,
        });
        this.pdfDialogOpen = false;
        this.pdfClientName = '';
        this.pdfClientEmail = '';
      } catch (e) {
        Vue.$toast.error('Could not generate PDF');
      } finally {
        this.generatingPdf = false;
      }
    },
    async sendEmail() {
      const em = validateEmail(this.customerEmail);
      if (!em.valid) {
        Vue.$toast.error(em.message);
        return;
      }
      this.sending = true;
      try {
        const body = buildPaQuoteEmailHtml(this.quote, this.customerName, {
          agentName: this.agentName,
        });
        if (this.mockEnabled) {
          await new Promise((r) => { setTimeout(r, 600); });
          Vue.$toast.success(`Quote sent to ${em.normalized} (demo)`);
        } else {
          await axios.post('/customer/digital-notification/client-api/v1/emails', {
            to: em.normalized,
            subject: `Students PA Quote — ${this.optionName} | Pacis Insurance`,
            body,
          }, { _skipErrorRedirect: true });
          Vue.$toast.success(`Quote emailed to ${em.normalized}`);
        }
        this.emailOpen = false;
        this.customerName = '';
        this.customerEmail = '';
      } catch (e) {
        Vue.$toast.error('Failed to send email');
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>

<style scoped>
.quote-live-card {
  background: linear-gradient(135deg, #1E4276 0%, #2A5A9E 100%);
  color: white;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 8px 24px rgba(30, 66, 118, 0.22);
}
.card-title-label {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
}
.checkout-meta {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
}
.checkout-meta-row {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.75);
}
.checkout-meta-row span:last-child {
  font-weight: 700;
  color: white;
}
.sidebar-actions { display: flex; gap: 6px; margin-top: 14px; }
.sidebar-action-btn {
  flex: 1;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  padding: 8px 6px;
  color: white;
  font-size: 0.68rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
}
.sidebar-action-btn:hover { background: rgba(255, 255, 255, 0.2); }
.cta-bar { display: flex; flex-direction: column; gap: 8px; margin-top: 14px; }
.btn-generate {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  color: #1E4276;
  font-weight: 700;
  font-size: 0.85rem;
  padding: 12px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
}
.btn-generate:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-generate.generated { background: rgba(255, 255, 255, 0.15); color: white; cursor: default; }
.btn-continue {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #2BABE2;
  color: white;
  font-weight: 700;
  font-size: 0.85rem;
  padding: 12px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
}
.btn-continue:hover { background: #1e94c8; }
/* Dialog — motor eq-* styles */
.eq-dialog { background: white; border-radius: 12px; overflow: hidden; }
.eq-header {
  background: linear-gradient(135deg, #1E4276, #2A5A9E);
  padding: 18px 20px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  position: relative;
}
.eq-header-icon {
  width: 40px; height: 40px; border-radius: 10px;
  background: rgba(255, 255, 255, 0.15);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.eq-title { margin: 0; font-size: 1rem; font-weight: 700; color: white; }
.eq-subtitle { margin: 2px 0 0; font-size: 0.75rem; color: rgba(255, 255, 255, 0.7); }
.eq-close {
  position: absolute; top: 14px; right: 14px;
  background: rgba(255, 255, 255, 0.15); border: none; border-radius: 50%;
  width: 28px; height: 28px; cursor: pointer; display: flex; align-items: center; justify-content: center;
}
.eq-body { padding: 20px; }
.eq-field { margin-bottom: 14px; }
.eq-label { font-size: 0.78rem; font-weight: 600; color: #64748B; display: block; margin-bottom: 6px; }
.eq-req { color: #E53E3E; }
.eq-input {
  width: 100%; padding: 10px 12px; border: 1.5px solid #E2E8F0; border-radius: 8px;
  font-size: 0.9rem; color: #1E4276; box-sizing: border-box;
}
.eq-input:focus { outline: none; border-color: #00AEEF; box-shadow: 0 0 0 3px rgba(0, 174, 239, 0.12); }
.eq-footer { padding: 0 20px 20px; display: flex; gap: 10px; justify-content: flex-end; }
.eq-btn-cancel {
  padding: 10px 18px; border-radius: 8px; border: 1px solid #E2E8F0;
  background: white; font-weight: 600; color: #64748B; cursor: pointer;
}
.eq-btn-send {
  padding: 10px 18px; border-radius: 8px; border: none;
  background: #00AEEF; font-weight: 700; color: #1E4276; cursor: pointer;
}
.eq-btn-send:disabled { opacity: 0.5; cursor: not-allowed; }
.eq-hint { font-size: 0.75rem; color: #94a3b8; margin: 0; line-height: 1.4; }

@media (max-width: 600px) {
  .sidebar-actions {
    flex-direction: column;
  }
  .sidebar-action-btn {
    min-height: 40px;
    font-size: 0.75rem;
  }
  .eq-footer {
    flex-direction: column-reverse;
    gap: 8px;
  }
  .eq-btn-cancel,
  .eq-btn-send {
    width: 100%;
    justify-content: center;
  }
}
</style>
